"use client"

import { protectedFetch } from "@/lib/turnstile-client";

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog"
import { AnimatePresence } from "framer-motion"
import { toast } from "sonner"
import { Progress } from "@/components/ui/progress"
import { ErrorBoundary } from 'react-error-boundary'

interface ContactModalProps {
  children: React.ReactNode
  open: boolean
  onClose: () => void
  initialService?: string | null
}

type ContactType = 'project' | 'quote' | 'general' | null

type FormField = {
  id: string;
  question: string;
  type: 'text' | 'email' | 'textarea' | 'select';
  placeholder: string;
  value: string;
  options?: { value: string; label: string }[];
  helpText?: string;
}

interface FormData {
  name: string;
  email: string;
  company?: string;
  projectType?: string;
  service?: string;
  timeline?: string;
  budget?: string;
  message?: string;
  subject?: string;
}

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

function ErrorFallback({ error }: { error: unknown }) {
  return (
    <div className="p-4 bg-red-50 text-red-600 rounded-lg">
      <p>Something went wrong:</p>
      <pre className="mt-2 text-sm">{error instanceof Error ? error.message : "Something went wrong."}</pre>
    </div>
  )
}

export default function ContactModal({ children, open, onClose, initialService }: ContactModalProps) {
  const [currentStep, setCurrentStep] = useState(-1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [direction, setDirection] = useState(0)
  const [contactType, setContactType] = useState<ContactType>(null)
  const [formFields, setFormFields] = useState<FormField[]>([])
  const [showThankYou, setShowThankYou] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)

  // Initialize with project type and service if provided
  useEffect(() => {
    if (open && initialService) {
      setContactType('project');
      const fields = getFormFieldsByType('project');
      fields.forEach(field => {
        if (field.id === 'service') {
          field.value = initialService;
        }
      });
      setFormFields(fields);
      setCurrentStep(0);
    }
  }, [open, initialService]);

  // Get form fields based on contact type
  useEffect(() => {
    if (contactType) {
      const fields = getFormFieldsByType(contactType);
      setFormFields(fields);
    }
  }, [contactType]);

  // Update email field question when name is entered
  useEffect(() => {
    if (currentStep === 1 && formFields[0]?.value) {
      const name = formFields[0].value;
      setFormFields(prev => {
        const newFields = [...prev];
        if (newFields[1]) {
          newFields[1] = {
            ...newFields[1],
            question: `Hi ${name}! How can we reach you?`
          };
        }
        return newFields;
      });
    }
  }, [currentStep, formFields]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setCurrentStep(-1)
      setContactType(null)
      setFormFields([])
      setShowThankYou(false)
      setEmailError(null)
    }
  }, [open])

  const handleInputChange = (value: string) => {
    setFormFields(prev => {
      const newFields = [...prev];
      if (newFields[currentStep]) {
        newFields[currentStep] = {
          ...newFields[currentStep],
          value,
        };
        if (newFields[currentStep].type === 'email') {
          setEmailError(null);
        }
      }
      return newFields;
    });
  }

  const handleNext = () => {
    const currentField = formFields[currentStep];
    
    if (currentField?.type === 'email') {
      if (!validateEmail(currentField.value)) {
        setEmailError('Please enter a valid email address');
        return;
      }
    }

    if (currentStep < formFields.length - 1) {
      setDirection(1)
      setCurrentStep(prev => prev + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep === 0) {
      setContactType(null)
      setCurrentStep(-1)
      setDirection(-1)
    } else if (currentStep > 0) {
      setDirection(-1)
      setCurrentStep(prev => prev - 1)
    }
  }

  const handleContactTypeSelect = (type: ContactType) => {
    setContactType(type)
    setCurrentStep(0)
    setDirection(1)
  }

  const validateForm = () => {
    const currentFormData = formFields.reduce((acc, field) => ({
      ...acc,
      [field.id]: field.value
    }), {}) as FormData;

    if (!currentFormData.name?.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!currentFormData.email?.trim()) {
      toast.error('Please enter your email');
      return false;
    }
    if (!validateEmail(currentFormData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    return true;
  }

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const currentFormData = formFields.reduce((acc, field) => ({
        ...acc,
        [field.id]: field.value
      }), {}) as FormData;

      const response = await protectedFetch('/api/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formData: {
            name: currentFormData.name || '',
            email: currentFormData.email || '',
            company: currentFormData.company || '',
            project_type: currentFormData.projectType || '',
            service: currentFormData.service || '',
            timeline: currentFormData.timeline || '',
            budget: currentFormData.budget || '',
            message: currentFormData.message || '',
            subject: currentFormData.subject || ''
          },
          formType: contactType || 'project',
          to: currentFormData.email || ''
        }),
      }, "contact");

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setShowThankYou(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
    }
  }

  const currentField = currentStep >= 0 ? formFields[currentStep] : null
  const progress = currentStep === -1 ? 0 : ((currentStep + 1) / formFields.length) * 100

  const getFormFieldsByType = (type: ContactType): FormField[] => {
    const baseFields = [
      {
        id: 'name',
        question: "Hi there! We're excited to learn about your ideas. What's your name?",
        type: 'text' as const,
        placeholder: 'Your name',
        value: '',
        helpText: 'We love putting a name to the face!'
      },
      {
        id: 'email',
        question: "Great to meet you! How can we reach you?",
        type: 'email' as const,
        placeholder: 'Your best email',
        value: '',
        helpText: 'We will keep you in the loop with project updates'
      }
    ]

    switch (type) {
      case 'project':
        return [
          ...baseFields,
          {
            id: 'company',
            question: "Tell us about where you work!",
            type: 'text' as const,
            placeholder: 'Your company name',
            value: '',
            helpText: 'Helps us understand your world better'
          },
          {
            id: 'project_type',
            question: "What kind of project did you have in mind?",
            type: 'select' as const,
            placeholder: 'Choose your project type',
            value: '',
            options: [
              { value: 'web_design', label: 'Web Design & Development' },
              { value: 'visual_design', label: 'Visual Design' },
              { value: 'digital_products', label: 'Digital Products' },
              { value: 'ai_solutions', label: 'AI Solutions' },
              { value: 'customer_experience', label: 'Customer Experience' },
              { value: 'other', label: 'Something else entirely!' }
            ],
            helpText: 'No idea is too big or small - we are here to help!'
          },
          {
            id: 'timeline',
            question: "When would you like to get started?",
            type: 'select' as const,
            placeholder: 'Pick a timeline',
            value: '',
            options: [
              { value: 'immediately', label: 'Right away!' },
              { value: 'next_month', label: 'In the next month' },
              { value: 'next_quarter', label: 'Coming quarter' },
              { value: 'six_months', label: 'Later this year' },
              { value: 'exploring', label: 'Just exploring for now' }
            ],
            helpText: 'Helps us plan the perfect timeline for you'
          },
          {
            id: 'budget',
            question: "What budget range are you thinking?",
            type: 'select' as const,
            placeholder: 'Select a range',
            value: '',
            options: [
              { value: 'under_10k', label: 'Under $10,000' },
              { value: '10k_25k', label: '$10,000 - $25,000' },
              { value: '25k_50k', label: '$25,000 - $50,000' },
              { value: '50k_100k', label: '$50,000 - $100,000' },
              { value: '100k_plus', label: '$100,000+' },
              { value: 'not_sure', label: 'Still figuring it out' }
            ],
            helpText: 'Helps us suggest the best options for your investment'
          },
          {
            id: 'message',
            question: "What's your vision? Tell us all about it!",
            type: 'textarea' as const,
            placeholder: 'Share your dreams, goals, and any cool features you want...',
            value: '',
            helpText: 'The more you share, the better we can bring your vision to life'
          }
        ]
      case 'quote':
        return [
          ...baseFields,
          {
            id: 'service',
            question: "Which of our services caught your eye?",
            type: 'select' as const,
            placeholder: 'Pick a service',
            value: '',
            options: [
              { value: 'web_design', label: 'Web Design & Development' },
              { value: 'visual_design', label: 'Visual Design' },
              { value: 'digital_products', label: 'Digital Products' },
              { value: 'ai_solutions', label: 'AI Solutions' },
              { value: 'customer_experience', label: 'Customer Experience' },
              { value: 'other', label: 'Something else!' }
            ],
            helpText: 'Let us know what interests you'
          },
          {
            id: 'budget',
            question: "What's your ideal budget range?",
            type: 'select' as const,
            placeholder: 'Select a range',
            value: '',
            options: [
              { value: 'under_10k', label: 'Under $10,000' },
              { value: '10k_25k', label: '$10,000 - $25,000' },
              { value: '25k_50k', label: '$25,000 - $50,000' },
              { value: '50k_100k', label: '$50,000 - $100,000' },
              { value: '100k_plus', label: '$100,000+' },
              { value: 'not_sure', label: 'Not sure yet - and that is okay!' }
            ],
            helpText: 'Helps us customize the perfect solution for you'
          },
          {
            id: 'timeline',
            question: "When would you like to kick things off?",
            type: 'select' as const,
            placeholder: 'Choose your timeline',
            value: '',
            options: [
              { value: 'urgent', label: 'As soon as possible!' },
              { value: 'month', label: 'Within a month' },
              { value: 'quarter', label: 'This quarter' },
              { value: 'flexible', label: 'Flexible on timing' }
            ],
            helpText: 'Helps us prepare for your project'
          },
          {
            id: 'message',
            question: "Tell us more about what you need!",
            type: 'textarea' as const,
            placeholder: 'Share your ideas, requirements, or any questions...',
            value: '',
            helpText: 'No detail is too small - we are all ears!'
          }
        ]
      case 'general':
        return [
          ...baseFields,
          {
            id: 'subject',
            question: "What brings you by today?",
            type: 'select' as const,
            placeholder: 'Choose a topic',
            value: '',
            options: [
              { value: 'partnership', label: 'Let us collaborate!' },
              { value: 'information', label: 'Need some info' },
              { value: 'support', label: 'Looking for help' },
              { value: 'other', label: 'Something else' }
            ],
            helpText: 'Helps us point you in the right direction'
          },
          {
            id: 'message',
            question: "What's on your mind?",
            type: 'textarea' as const,
            placeholder: 'Share your thoughts with us...',
            value: '',
            helpText: 'We are here to chat about anything!'
          }
        ]
      default:
        return baseFields
    }
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          {children}
        </DialogTrigger>
        <DialogContent className="sm:max-w-[800px] bg-white border-[#171A20]/10 max-h-[90vh] overflow-y-auto rounded-lg px-6 py-4 sm:px-8 sm:py-6">
          <DialogHeader>
            <DialogTitle className="text-[#171A20]">Get in Touch</DialogTitle>
            <DialogDescription className="text-[#171A20]/60">
              Choose how you&apos;d like to connect with us
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Progress value={100} backgroundColor="bg-gray-200" />
            <Progress value={progress} backgroundColor="bg-[#171A20]" className="absolute top-0" />
          </div>

          <div className="relative h-auto min-h-[300px] overflow-hidden">
            <AnimatePresence initial={false} custom={direction}>
              {showThankYou ? (
                <div className="text-center space-y-4">
                  <div className="flex justify-center">
                    <svg className="w-12 h-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-semibold text-[#171A20]">Thank you!</h2>
                  <p className="text-[#171A20]/60">We&apos;ve received your message and will be in touch shortly.</p>
                  <p className="text-sm text-[#171A20]/40">You&apos;ll receive a confirmation email shortly.</p>
                </div>
              ) : currentStep === -1 ? (
                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl font-semibold text-[#171A20] leading-tight">
                    How can we help you today?
                  </h2>
                  <div className="space-y-3">
                    <Button
                      onClick={() => handleContactTypeSelect('project')}
                      className="w-full h-auto p-4 bg-[#171A20]/5 hover:bg-[#171A20]/10 border border-[#171A20]/10 hover:border-[#171A20] flex flex-col items-start text-left transition-all duration-200"
                    >
                      <span className="text-lg font-semibold text-[#171A20]">Project Inquiry</span>
                      <span className="text-sm text-[#171A20]/60">Start a new project or discuss an idea</span>
                    </Button>
                    <Button
                      onClick={() => handleContactTypeSelect('quote')}
                      className="w-full h-auto p-4 bg-[#171A20]/5 hover:bg-[#171A20]/10 border border-[#171A20]/10 hover:border-[#171A20] flex flex-col items-start text-left transition-all duration-200"
                    >
                      <span className="text-lg font-semibold text-[#171A20]">Get a Quote</span>
                      <span className="text-sm text-[#171A20]/60">Request pricing for our services</span>
                    </Button>
                    <Button
                      onClick={() => handleContactTypeSelect('general')}
                      className="w-full h-auto p-4 bg-[#171A20]/5 hover:bg-[#171A20]/10 border border-[#171A20]/10 hover:border-[#171A20] flex flex-col items-start text-left transition-all duration-200"
                    >
                      <span className="text-lg font-semibold text-[#171A20]">General Inquiry</span>
                      <span className="text-sm text-[#171A20]/60">Ask a question or leave a message</span>
                    </Button>
                  </div>
                </div>
              ) : currentField ? (
                <div className="space-y-6">
                  <h2 
                    className="text-2xl sm:text-3xl font-semibold text-[#171A20] leading-tight"
                    dangerouslySetInnerHTML={{ __html: currentField.question }}
                  />
                  {currentField.type === 'select' ? (
                    <div className="space-y-3">
                      {currentField.options?.map((option) => (
                        <Button
                          key={option.value}
                          onClick={() => handleInputChange(option.value)}
                          className={`w-full h-auto p-4 bg-[#171A20]/5 hover:bg-[#171A20]/10 border ${
                            currentField.value === option.value
                              ? 'border-[#171A20] bg-[#171A20]/10'
                              : 'border-[#171A20]/10 hover:border-[#171A20]'
                          } flex flex-col items-start text-left transition-all duration-200`}
                        >
                          <span className="text-lg font-semibold text-[#171A20]">{option.label}</span>
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Input
                        type={currentField.type}
                        value={currentField.value}
                        onChange={(e) => handleInputChange(e.target.value)}
                        placeholder={currentField.placeholder}
                        className={`h-12 sm:h-14 bg-[#171A20]/5 border-[#171A20]/10 focus:border-[#171A20] text-[#171A20] placeholder:text-[#171A20]/40 text-base sm:text-lg transition-all duration-200 ${
                          emailError ? 'border-red-500' : ''
                        }`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && currentStep < formFields.length - 1) {
                            e.preventDefault();
                            handleNext();
                          }
                        }}
                      />
                      {emailError && (
                        <p className="text-red-500 text-sm">{emailError}</p>
                      )}
                      {currentField.helpText && (
                        <p className="text-sm text-[#171A20]/60">{currentField.helpText}</p>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
            </AnimatePresence>
          </div>

          {!showThankYou && (
            <div className="flex justify-between items-center mt-6 sm:mt-8 pt-4 border-t border-[#171A20]/10">
              <Button
                type="button"
                variant="ghost"
                onClick={handlePrevious}
                disabled={currentStep === -1}
                className="text-[#171A20]/70 hover:text-[#171A20] hover:bg-[#171A20]/5 text-sm sm:text-base"
              >
                Back
              </Button>

              {currentStep === formFields.length - 1 ? (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !currentField?.value}
                  className="bg-[#171A20] hover:bg-[#171A20]/90 text-white px-6 sm:px-8 text-sm sm:text-base"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Sending...</span>
                    </div>
                  ) : (
                    <span>Submit</span>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  disabled={!currentField?.value}
                  className="bg-[#171A20] hover:bg-[#171A20]/90 text-white px-6 sm:px-8 text-sm sm:text-base"
                >
                  Next
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ErrorBoundary>
  )
}